import React, { useContext, useEffect, useState } from 'react';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import LandContractType from 'src/models/LandContractType';
import useFetch from 'use-http';
import { FormEvent } from 'react';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import DocumentTemplate from 'src/models/DocumentTemplate';
import DocGenerationComponent from './DocGenerationComponent';

const docList: {id: number; name: string; templates: { id: number; name: string; }[]}[] = [
    {
        id: 1,
        name: 'Lease',
        templates: [],
    },
    {
        id: 2,
        name: 'LPR',
        templates: [],
    },
    {
        id: 3,
        name: 'MEMO',
        templates: [],
    },
    {
        id: 4,
        name: 'OOP',
        templates: [],
    },
];

interface IGenerateDocListProps {
    leaseFunction: (id: number) => void;
    lprFunction: (id: number) => void;
    leasePacketFunc?: () => void;
    memoFunction?: (id: number) => void;
    oopFunction?: (id: number) => void;
    isNewLease?: boolean;
    contractType?: LandContractType;
    leaseId?: number;
    leaseNumber?: number;
    uploadReportFileCallback?: (files: File[]) => void;
    updateOlderVersions?: (hasOlder: boolean) => void;
    filesToUpload?: File[];
}

type GenerateDocListProps = IGenerateDocListProps;

const GenerateDocs = (props: GenerateDocListProps) => {
    const { get, response } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [leaseId, setLeaseId] = useState<number>(undefined);
    const [memoId, setMemoId] = useState<number>(undefined);
    const [oopId, setOopId] = useState<number>(undefined);
    
    const [templateList, setTemplateList] = useState<Array<DocumentTemplate>>([]);
    const [customDocList, setCustomDocList] = useState<{id: number; name: string; templates: { id: number; name: string; }[]}[]>([]);
    const [loadingLPR, setLoadingLPR] = useState<boolean>(false);
    const [uploadLPR, setuploadLPR] = useState<boolean>(false);
    const [lprOutputType, setLPROutputType] = useState(1);

    useEffect(() => {
        getDocumentTemplates();
    }, []);

    useEffect(() => {
        let newDocList = [...docList];
        let hasOlderVersion = false;

        newDocList.forEach(doc => {
            doc.templates.length = 0;
        });

        for (let template of templateList) {
            if (template.version < 3) {
                hasOlderVersion = true;
            }

            if (props.contractType === template.contractTypeReferenceNumber) {
                switch (template.docType) {
                    case 'Lease Template':
                        newDocList[0].templates.push({ id: template.referenceNumber, name: template.name });
                        break;
                    case 'Memo':
                        newDocList[2].templates.push({ id: template.referenceNumber, name: template.name });
                        break;
                    case 'Order of Payment':
                        newDocList[3].templates.push({ id: template.referenceNumber, name: template.name });
                        break;
                    default:
                        break;
                }
            }
        }

        props.updateOlderVersions(hasOlderVersion);
        setCustomDocList(newDocList);
    }, [templateList]);

    const handleUploadLPRChange = (e: FormEvent<HTMLInputElement>) => {
        setuploadLPR(e.currentTarget.checked);
    };

    const getLeaseReportFile = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        if (!loadingLPR) {
            setLoadingLPR(true);

            await get(`v1.0/reports/GetReport/${lprOutputType}/globalId/0/contractLookupId/0/leaseNumber/${props?.leaseNumber}`);
            let fileBlob: BlobPart = undefined;
            if (response.ok) {
                await response.blob().then((blob) => {
                    fileBlob = blob;
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.getElementById('reportFileBlob') as HTMLAnchorElement;
                    a.href = blobUrl;
                    a.download = lprOutputType === 1 ? `${props.leaseNumber}-LPR.xlsx` : `${props.leaseNumber}-LPR.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(blobUrl);
                });
            }
            if (uploadLPR) {
                console.log('Would upload to S3');
                let uploadFile = new File([fileBlob], lprOutputType === 1 ? `${props.leaseNumber}-LPR.xlsx` : `${props.leaseNumber}-LPR.pdf`);
                props.uploadReportFileCallback([uploadFile]);
            }
            setLoadingLPR(false);
        }
    };

    const getDocumentTemplates = async () => {
        let result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/by-project/all`);

        if (response.ok) {
            setTemplateList(result);
        }
    };

    const renderDocTemplate = (label: string) => {
        if (label === 'Lease' && !!leaseId) {
            return props.leaseFunction(leaseId);
        }
        if (label === 'MEMO' && !!memoId) {
            return props.memoFunction(memoId);
        }
        if (label === 'OOP' && !!oopId) {
            return props.oopFunction(oopId);
        }
    };

    const lprOptions = () => {
        return (
            <>
                <div style={{ display: 'flex' }}>
                    <div>
                        <RHFCheckbox
                            style={{verticalAlign: 'middle'}}
                            label={'Automatically upload'}
                            name={'uploadToS3'}
                            onClick={handleUploadLPRChange}
                        />
                    </div>
                    <span style={{ position: 'relative', display: 'inline-block', overflow: 'hidden', width: loadingLPR ? 'auto' : 0, marginLeft: '.5rem' }}>
                        <i className="fas fa-spinner fa-spin" />
                    </span>
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', top: '100%', transform: 'translateX(-120%)', display: 'flex' }}>
                            <input type="radio" id="excel" name="mor_output_type" onChange={() => setLPROutputType(1)} checked={lprOutputType === 1} />
                            <label htmlFor="excel">Excel</label>

                            <div style={{ margin: '0 1rem' }}>|</div>

                            <input type="radio" id="pdf" name="mor_output_type" onChange={() => setLPROutputType(2)} checked={lprOutputType === 2} />
                            <label htmlFor="pdf">PDF</label>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const onSetTemplateIds = (templateId: number, docId: number) => {
        switch (docId) {
            case 3:
                setMemoId(templateId);
                break;
            case 4:
                setOopId(templateId);
                break;
            case 1:
            default:
                setLeaseId(templateId);
                break;
        }
    };

    const generateDocList = () => {
        return customDocList.map((doc, index) => {
            if (doc.name === 'LPR') {
                return (
                    <div key={index} style={{ marginTop: '1.5rem', border: '1px solid grey', boxShadow: '5px 5px 3px #aaaaaa', padding: '2rem' }}>
                        <div>{doc.name}</div>
                        <div
                            style={{
                                marginLeft: '1rem',
                                marginTop: '1rem',
                                display: 'flex',
                                rowGap: '1rem',
                                alignItems: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            {lprOptions()}
                            <a style={{ display: 'hidden' }} id="reportFileBlob" />
                            <div
                                onClick={getLeaseReportFile}
                                style={{ transform: 'translateY(35%)'}}
                                className={`dlc-form-button-default medium primary ${loadingLPR || props?.isNewLease ? 'disabled' : ''}`}
                            >
                                {`Generate`}
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <DocGenerationComponent
                    key={index.toString()}
                    customDoc={doc}
                    disabled={props.isNewLease}
                    setTemplateId={(templateId: number) => onSetTemplateIds(templateId, doc.id)}
                    renderDocTemplate={() => renderDocTemplate(doc.name)}
                />
            );
        });
    };

    return (
        <div>
            <div style={{ fontSize: '1.5rem', textAlign: 'center', marginTop: '1rem' }}>Generate Documents</div>

            {customDocList.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    {generateDocList()}
                </div>
            )}
            {customDocList.length === 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    No documents have been selected to be generated
                </div>
            )}
        </div>
    );
};

export default GenerateDocs;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/GenerateDocs.tsx